import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { getChartOfAccounts } from '../../../services/accountService';
import { formatCurrency } from '../../../utils/currency';

const JournalEntryModal = ({ isOpen, onClose, onSubmit, entry = null }) => {
  const [accounts, setAccounts] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [formData, setFormData] = useState({
    entryDate: new Date().toISOString().split('T')[0],
    reference: '',
    description: '',
    lineItems: [
      { account: '', debit: '', credit: '', memo: '' },
      { account: '', debit: '', credit: '', memo: '' }
    ]
  });

  // Load accounts on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const accountsData = await getChartOfAccounts();
        
        setAccounts(accountsData);
        
        // Flatten accounts for dropdown options
        const flattenAccounts = (accountList) => {
          let result = [];
          accountList?.forEach(account => {
            result.push({
              value: account.id,
              label: `${account.code} - ${account.name}`
            });
            if (account.children && account.children.length > 0) {
              result = result.concat(flattenAccounts(account.children));
            }
          });
          return result;
        };
        
        setAccountOptions(flattenAccounts(accountsData));
      } catch (error) {
        console.error('Error loading data:', error);
        // Fallback to hardcoded options if API fails
        setAccountOptions([
          { value: '1000', label: '1000 - Cash' },
          { value: '1100', label: '1100 - Accounts Receivable' },
          { value: '1300', label: '1300 - Prepaid Insurance' },
          { value: '1500', label: '1500 - Equipment' },
          { value: '1510', label: '1510 - Accumulated Depreciation' },
          { value: '2000', label: '2000 - Accounts Payable' },
          { value: '2200', label: '2200 - Salaries Payable' },
          { value: '2300', label: '2300 - Interest Payable' },
          { value: '2400', label: '2400 - Unearned Revenue' },
          { value: '3000', label: '3000 - Owner\'s Equity' },
          { value: '4100', label: '4100 - Service Revenue' },
          { value: '5100', label: '5100 - Salaries Expense' },
          { value: '5200', label: '5200 - Insurance Expense' },
          { value: '5300', label: '5300 - Interest Expense' },
          { value: '6100', label: '6100 - Depreciation Expense' }
        ]);
      }
    };
    
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (entry) {
      setFormData({
        entryDate: entry?.entryDate,
        reference: entry?.reference,
        description: entry?.description,
        lineItems: entry?.lineItems?.map(item => ({
          account: item?.account?.split(' - ')?.[0],
          debit: item?.debit || '',
          credit: item?.credit || '',
          memo: item?.memo
        }))
      });
    }
  }, [entry]);

  const handleAddLineItem = () => {
    setFormData({
      ...formData,
      lineItems: [...formData?.lineItems, { account: '', debit: '', credit: '', memo: '' }]
    });
  };

  const handleRemoveLineItem = (index) => {
    if (formData?.lineItems?.length > 2) {
      const newLineItems = formData?.lineItems?.filter((_, i) => i !== index);
      setFormData({ ...formData, lineItems: newLineItems });
    }
  };

  const handleLineItemChange = (index, field, value) => {
    const newLineItems = [...formData?.lineItems];
    newLineItems[index][field] = value;

    if (field === 'debit' && value) {
      newLineItems[index].credit = '';
    } else if (field === 'credit' && value) {
      newLineItems[index].debit = '';
    }

    setFormData({ ...formData, lineItems: newLineItems });
  };

  const calculateTotalDebit = () => {
    return formData?.lineItems?.reduce((sum, item) => {
      const debit = parseFloat(item?.debit) || 0;
      return sum + debit;
    }, 0);
  };

  const calculateTotalCredit = () => {
    return formData?.lineItems?.reduce((sum, item) => {
      const credit = parseFloat(item?.credit) || 0;
      return sum + credit;
    }, 0);
  };

  const isBalanced = () => {
    const totalDebit = calculateTotalDebit();
    const totalCredit = calculateTotalCredit();
    return Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;
  };

  const getDifference = () => {
    return calculateTotalDebit() - calculateTotalCredit();
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (isBalanced()) {
      onSubmit(formData);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">
            {entry ? 'Edit Journal Entry' : 'Create Journal Entry'}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-4 md:p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Entry Date"
                type="date"
                value={formData?.entryDate}
                onChange={(e) => setFormData({ ...formData, entryDate: e?.target?.value })}
                required
              />
              <Input
                label="Reference"
                placeholder="REF-001"
                value={formData?.reference}
                onChange={(e) => setFormData({ ...formData, reference: e?.target?.value })}
                required
              />
              <Input
                label="Description"
                placeholder="Entry description"
                value={formData?.description}
                onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                required
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-lg text-foreground">Line Items</h3>
                <Button type="button" variant="outline" size="sm" onClick={handleAddLineItem}>
                  <Icon name="Plus" size={16} className="mr-2" />
                  Add Line
                </Button>
              </div>

              <div className="bg-muted/20 rounded-lg border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Account</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Debit</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-foreground">Credit</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Memo</th>
                        <th className="px-4 py-3 w-12"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {formData?.lineItems?.map((line, index) => (
                        <tr key={index} className="border-t border-border">
                          <td className="px-4 py-3">
                            <Select
                              options={accountOptions}
                              value={line?.account}
                              onChange={(value) => handleLineItemChange(index, 'account', value)}
                              placeholder="Select account"
                              searchable
                              required
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={line?.debit}
                              onChange={(e) => handleLineItemChange(index, 'debit', e?.target?.value)}
                              disabled={!!line?.credit}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={line?.credit}
                              onChange={(e) => handleLineItemChange(index, 'credit', e?.target?.value)}
                              disabled={!!line?.debit}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Input
                              placeholder="Optional memo"
                              value={line?.memo}
                              onChange={(e) => handleLineItemChange(index, 'memo', e?.target?.value)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => handleRemoveLineItem(index)}
                              disabled={formData?.lineItems?.length <= 2}
                            >
                              <Icon name="Trash2" size={16} className="text-error" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t-2 border-border bg-muted/30">
                        <td className="px-4 py-3 font-semibold text-foreground">Total</td>
                        <td className="px-4 py-3 text-right font-bold text-foreground">
                          {formatCurrency(calculateTotalDebit())}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-foreground">
                          {formatCurrency(calculateTotalCredit())}
                        </td>
                        <td colSpan="2"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={`p-4 rounded-lg border ${
                isBalanced()
                  ? 'bg-success/10 border-success text-success' :'bg-error/10 border-error text-error'
              }`}>
                <div className="flex items-center gap-2">
                  <Icon name={isBalanced() ? 'CheckCircle' : 'AlertCircle'} size={20} />
                  <div>
                    <p className="font-semibold">
                      {isBalanced() ? 'Entry is balanced' : 'Entry is not balanced'}
                    </p>
                    {!isBalanced() && (
                      <p className="text-sm mt-1">
                        Difference: {formatCurrency(Math.abs(getDifference()))} 
                        {getDifference() > 0 ? ' (Debit exceeds Credit)' : ' (Credit exceeds Debit)'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border bg-muted/20">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isBalanced()}>
              <Icon name="Save" size={16} className="mr-2" />
              {entry ? 'Update Entry' : 'Create Entry'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JournalEntryModal;